import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  LocalHospitalRounded,
  CheckCircleRounded,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { db } from 'services/firebase';
import { submitCheckIn } from 'services/clinicService';
import { claimVisitTci } from 'services/visitTciService';
import { derivePatientTciIds } from 'utils/visitTciStates';
import { masterChartPath } from 'constants/masterChartRoutes';

const PATIENT_TCI_LINKS = {
  triage: { label: 'Start CareStart triage', path: '/carestart3' },
  hpi: { label: 'Complete your history', path: '/carestart3' },
  medrec: { label: 'Review your medications', path: '/dashboard/medications' },
  'medrec-intake': { label: 'Medication intake', path: '/dashboard/medications' },
  allergies: { label: 'Review allergies', path: masterChartPath('allergies') },
  differential: { label: 'Continue with CareStart', path: '/carestart3' },
  'return-precautions': { label: 'Return precautions', path: '/carestart3' },
  'pain-assessment': { label: 'Pain assessment', path: '/carestart3' },
  'op-previsit-intake': { label: 'Pre-visit questionnaire', path: '/carestart3' },
  'op-registration': { label: 'Update demographics', path: masterChartPath('demographics') },
  'op-hpi': { label: 'History of present illness', path: '/carestart3' },
  'op-medrec': { label: 'Medication reconciliation', path: '/dashboard/medications' },
  'op-care-gap': { label: 'Care gap reminders', path: '/dashboard/care-gaps' },
  'op-chronic-fu': { label: 'Chronic follow-up', path: '/carestart3' },
};

/**
 * Public clinic check-in landing page (route: /checkin/:clinicId).
 *
 * After check-in, pending patient TCIs are derived from visitTciStates.
 * Starting a TCI claims it atomically so a concurrent clinician claim cannot also win.
 */
const CheckInPage = () => {
  const { clinicId } = useParams();
  const [searchParams] = useSearchParams();
  const departmentId = searchParams.get('dept') || undefined;
  const { user, userLoading } = useAuth();
  const navigate = useNavigate();

  const [clinicName, setClinicName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInError, setCheckInError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [visitId, setVisitId] = useState(null);
  const [visitTciStates, setVisitTciStates] = useState({});
  const [claimingTciId, setClaimingTciId] = useState(null);
  const [claimNotice, setClaimNotice] = useState(null);

  useEffect(() => {
    let active = true;
    if (!clinicId) {
      setError('This check-in link is missing a clinic.');
      setLoading(false);
      return undefined;
    }
    db.collection('locations')
      .doc(clinicId)
      .get()
      .then((snap) => {
        if (!active) return;
        if (!snap.exists) {
          setError('That clinic could not be found. Please check the link and try again.');
        } else {
          setClinicName(snap.data()?.title || 'this clinic');
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Something went wrong loading this clinic. Please try again.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clinicId]);

  const handleCheckIn = async () => {
    setCheckingIn(true);
    setCheckInError(null);
    try {
      const result = await submitCheckIn(clinicId, departmentId);
      setVisitId(result?.visitId || null);
      const states = result?.visitTciStates || {};
      setVisitTciStates(states);
      setSuccess(true);
    } catch (err) {
      setCheckInError(err.message);
    } finally {
      setCheckingIn(false);
    }
  };

  const patientTciIds = derivePatientTciIds(visitTciStates);
  const continueLinks = patientTciIds
    .map((tciId) => ({ tciId, ...(PATIENT_TCI_LINKS[tciId] || null) }))
    .filter((row) => row.path);

  const handleContinue = async (tciId, path) => {
    if (!clinicId || !visitId) {
      navigate(path);
      return;
    }
    setClaimingTciId(tciId);
    setClaimNotice(null);
    try {
      const result = await claimVisitTci({
        clinicId,
        visitId,
        tciId,
        party: 'patient',
      });
      if (result?.visitTciStates) {
        setVisitTciStates(result.visitTciStates);
      }
      if (result?.alreadyClaimed) {
        setClaimNotice(
          result.status === 'claimed_clinician' || result.status === 'completed'
            ? 'A clinician already started this step. It is no longer pending for you.'
            : 'This step is no longer available.',
        );
        return;
      }
      navigate(path);
    } catch (err) {
      setClaimNotice(err.message || 'Could not start this step.');
    } finally {
      setClaimingTciId(null);
    }
  };

  const renderBody = () => {
    if (loading || userLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (success) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <CheckCircleRounded sx={{ fontSize: 56, color: 'success.main' }} />
          <Typography variant="h6">You're checked in</Typography>
          <Typography variant="body2" color="text.secondary">
            You're checked in to {clinicName}. Any messages they send will appear
            in your Messages.
          </Typography>
          {claimNotice ? (
            <Alert severity="info" sx={{ width: '100%' }} onClose={() => setClaimNotice(null)}>
              {claimNotice}
            </Alert>
          ) : null}
          {continueLinks.length > 0 ? (
            <Box sx={{ width: '100%', textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Continue before your visit
              </Typography>
              <List disablePadding>
                {continueLinks.map((link) => (
                  <ListItemButton
                    key={link.tciId}
                    disabled={Boolean(claimingTciId)}
                    onClick={() => handleContinue(link.tciId, link.path)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        claimingTciId === link.tciId
                          ? `${link.label}…`
                          : link.label
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ) : null}
          <Button component={RouterLink} to="/dashboard/messages" variant="contained">
            Go to Messages
          </Button>
        </Stack>
      );
    }

    if (!user) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="body1">
            Please sign in to check in to {clinicName}.
          </Typography>
          <Button component={RouterLink} to="/auth" variant="contained">
            Sign in
          </Button>
          <Typography variant="caption" color="text.secondary">
            After signing in, reopen this check-in link to finish.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2} alignItems="center" textAlign="center">
        <Typography variant="body1">
          Check in to <strong>{clinicName}</strong> to let the clinic know you're here.
        </Typography>
        {checkInError && (
          <Alert severity="error" sx={{ width: '100%' }} onClose={() => setCheckInError(null)}>
            {checkInError}
          </Alert>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          loading={checkingIn}
          onClick={handleCheckIn}
        >
          Check in
        </LoadingButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <LocalHospitalRounded sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700} textAlign="center">
            Clinic Check-in
          </Typography>
        </Stack>
        {renderBody()}
      </Paper>
    </Box>
  );
};

export default CheckInPage;
